import { useCallback, useEffect, useRef, useState } from 'react';
import { signalRService } from '../services/signalr/SignalRService';
import { HubMethods, HubEvents } from '../services/signalr/types';
import { useUser } from '../context/UserContext';
import './CallPanel.css';

interface CallPanelProps {
  roomId: string;
}

interface CallMessage {
  id: string;
  fromName: string;
  content: string;
  mine: boolean;
  at: number;
}

interface ActiveCall {
  callId: string;
  peerUserId: string;
  peerName: string;
  messages: CallMessage[];
}

const RING_TIMEOUT_MS = 30_000;

/**
 * In-room "phone call": lets a player reach someone who's OUT of earshot,
 * but only with the other side's explicit approval (accept/decline ring).
 * The conversation itself is ephemeral by design — like a real call, it
 * lives only while the call is up; nothing is persisted.
 *
 * Transport: the server-validated SocialEvent envelope channel
 * (call_request / call_accept / call_decline / call_end / call_message),
 * delivered targeted-only to the two participants. Consent (who may ring me,
 * blocks, cooldowns) is enforced server-side; a denial is a generic
 * "unavailable" — the caller never learns why. Decline and ring-timeout are
 * deliberately indistinguishable ("no answer") so declining is shame-free.
 *
 * Initiation comes from PlayerProfileOverlay via the 'gamedate-call-request'
 * window event.
 */
export function CallPanel({ roomId }: CallPanelProps) {
  const { user } = useUser();
  const [incoming, setIncoming] = useState<{ callId: string; fromUserId: string; fromName: string } | null>(null);
  const [outgoing, setOutgoing] = useState<{ callId: string; toName: string } | null>(null);
  const [active, setActive] = useState<ActiveCall | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const outgoingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const incomingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const myId = user?.id != null ? String(user.id) : '';

  const showNote = useCallback((text: string) => {
    setNote(text);
    setTimeout(() => setNote(null), 4000);
  }, []);

  const clearOutgoing = useCallback(() => {
    if (outgoingTimerRef.current) clearTimeout(outgoingTimerRef.current);
    outgoingTimerRef.current = null;
    setOutgoing(null);
  }, []);

  const clearIncoming = useCallback(() => {
    if (incomingTimerRef.current) clearTimeout(incomingTimerRef.current);
    incomingTimerRef.current = null;
    setIncoming(null);
  }, []);

  const sendEnvelope = useCallback(
    async (type: string, payload: Record<string, unknown>): Promise<{ ok: boolean; [k: string]: unknown }> => {
      try {
        // The hub signature is SocialEvent(string roomId, string envelopeJson):
        // the OUTER envelope must be stringified too (same as SocialPlayPanel).
        const envelopeJson = JSON.stringify({ type, payloadJson: JSON.stringify(payload) });
        const json = await signalRService.invoke<string>(HubMethods.SocialEvent, roomId, envelopeJson);
        return typeof json === 'string' ? JSON.parse(json) : (json as { ok: boolean });
      } catch (e) {
        console.error(`[Call] ${type} failed:`, e);
        return { ok: false };
      }
    },
    [roomId]
  );

  // --- Initiation from PlayerProfileOverlay ---
  useEffect(() => {
    const onRequest = async (e: Event) => {
      const { userId, displayName } = (e as CustomEvent<{ userId: string; displayName: string }>).detail || {};
      if (!userId || active || outgoing) return;
      const resp = await sendEnvelope('call_request', { targetUserId: userId });
      if (resp.ok && typeof resp.callId === 'string') {
        setOutgoing({ callId: resp.callId, toName: displayName || 'them' });
        outgoingTimerRef.current = setTimeout(() => {
          setOutgoing(null);
          showNote('No answer');
        }, RING_TIMEOUT_MS);
      } else {
        showNote("They're unavailable right now");
      }
    };
    window.addEventListener('gamedate-call-request', onRequest);
    return () => window.removeEventListener('gamedate-call-request', onRequest);
  }, [active, outgoing, sendEnvelope, showNote]);

  // --- Incoming envelopes ---
  // Unlike potion/puzzle envelopes ({type, from, fromName, payloadJson,...}),
  // call_* envelopes arrive FLAT and targeted-only (never room-broadcast):
  //   call_request  {type, callId, from:{userId,displayName}}          → target only
  //   call_accept   {type, callId, participants:[{userId,displayName}×2]} → both
  //   call_decline  {type, callId}                                     → caller only
  //   call_end      {type, callId}                                     → both
  //   call_message  {type, callId, from:{userId,displayName}, content, sentAt} → both
  // Targeted delivery means receipt itself proves the event is for us.
  useEffect(() => {
    if (!myId) return;

    interface CallWireEnvelope {
      type: string;
      callId?: string;
      from?: { userId?: string; displayName?: string };
      participants?: { userId?: string; displayName?: string }[];
      content?: string;
      sentAt?: string;
    }

    const onSocialEvent = (...args: unknown[]) => {
      let envelope: CallWireEnvelope;
      try {
        envelope = typeof args[0] === 'string'
          ? (JSON.parse(args[0] as string) as CallWireEnvelope)
          : (args[0] as CallWireEnvelope);
      } catch {
        return;
      }
      if (!envelope?.type?.startsWith('call_')) return;

      const callId = String(envelope.callId ?? '');
      if (!callId) return;

      switch (envelope.type) {
        case 'call_request': {
          // Busy on another call → let it ring out (server expiry covers it).
          if (active) return;
          clearIncoming();
          setIncoming({
            callId,
            fromUserId: String(envelope.from?.userId ?? ''),
            fromName: envelope.from?.displayName || 'Someone',
          });
          incomingTimerRef.current = setTimeout(() => setIncoming(null), RING_TIMEOUT_MS);
          break;
        }
        case 'call_accept': {
          const peer = (envelope.participants ?? [])
            .map((p) => ({ userId: String(p.userId ?? ''), displayName: String(p.displayName ?? '') }))
            .find((p) => p.userId && p.userId !== myId);
          if (!peer) return;
          clearOutgoing();
          clearIncoming();
          setActive({ callId, peerUserId: peer.userId, peerName: peer.displayName || 'Player', messages: [] });
          break;
        }
        case 'call_decline': {
          if (outgoing && callId === outgoing.callId) {
            clearOutgoing();
            showNote('No answer'); // indistinguishable from timeout, on purpose
          }
          break;
        }
        case 'call_end': {
          if (active && callId === active.callId) {
            setActive(null);
            showNote('Call ended');
          }
          if (incoming && callId === incoming.callId) clearIncoming();
          if (outgoing && callId === outgoing.callId) clearOutgoing();
          break;
        }
        case 'call_message': {
          if (!active || callId !== active.callId) return;
          const content = String(envelope.content ?? '');
          if (!content) return;
          const mine = String(envelope.from?.userId ?? '') === myId;
          setActive((prev) =>
            prev && prev.callId === callId
              ? {
                  ...prev,
                  messages: [
                    ...prev.messages,
                    {
                      id: `${envelope.sentAt ?? Date.now()}-${prev.messages.length}`,
                      fromName: mine ? 'You' : envelope.from?.displayName || prev.peerName,
                      content,
                      mine,
                      at: Date.now(),
                    },
                  ],
                }
              : prev
          );
          break;
        }
      }
    };

    signalRService.on(HubEvents.SocialEventReceived, onSocialEvent);
    return () => {
      signalRService.off(HubEvents.SocialEventReceived, onSocialEvent);
    };
  }, [myId, active, incoming, outgoing, clearIncoming, clearOutgoing, showNote]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [active?.messages.length]);

  const accept = async () => {
    if (!incoming) return;
    const resp = await sendEnvelope('call_accept', { callId: incoming.callId });
    if (!resp.ok) {
      showNote('Call is no longer available');
    }
    clearIncoming(); // call_accept broadcast opens the window for both sides
  };

  const decline = async () => {
    if (!incoming) return;
    await sendEnvelope('call_decline', { callId: incoming.callId });
    clearIncoming();
  };

  const cancelOutgoing = async () => {
    if (!outgoing) return;
    await sendEnvelope('call_end', { callId: outgoing.callId });
    clearOutgoing();
  };

  const hangUp = async () => {
    if (!active) return;
    await sendEnvelope('call_end', { callId: active.callId });
    setActive(null);
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const content = draft.trim();
    if (!content || !active) return;
    setDraft('');
    const resp = await sendEnvelope('call_message', { callId: active.callId, content });
    if (!resp.ok) showNote('Message not delivered');
  };

  if (!user) return null;

  return (
    <>
      {note && <div className="call-note">{note}</div>}

      {incoming && (
        <div className="call-ring-toast">
          <span className="call-ring-text">📞 {incoming.fromName} is calling…</span>
          <button className="call-btn accept" onClick={accept}>Accept</button>
          <button className="call-btn decline" onClick={decline}>Decline</button>
        </div>
      )}

      {outgoing && (
        <div className="call-ring-toast outgoing">
          <span className="call-ring-text">📞 Calling {outgoing.toName}…</span>
          <button className="call-btn decline" onClick={cancelOutgoing}>Cancel</button>
        </div>
      )}

      {active && (
        <div className="call-window">
          <div className="call-window-header">
            <span>📞 {active.peerName}</span>
            <button className="call-btn decline" onClick={hangUp}>End</button>
          </div>
          <div className="call-window-messages">
            {active.messages.length === 0 && (
              <div className="call-window-empty">You're connected — say something!</div>
            )}
            {active.messages.map((m) => (
              <div key={m.id} className={`call-msg ${m.mine ? 'mine' : ''}`}>
                <span className="call-msg-content">{m.content}</span>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <form className="call-window-input" onSubmit={sendMessage}>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={`Message ${active.peerName}…`}
              maxLength={2000}
            />
            <button type="submit" disabled={!draft.trim()}>Send</button>
          </form>
        </div>
      )}
    </>
  );
}
